import { useDeviceType } from '@cian/ui-kit/responsive';
import { equals, pathOr } from 'ramda';
import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { useMapZoom } from './useMapZoom';
import { useMaxPinsPerCluster } from './useMaxPinsPerCluster';
import { getFeatureWithSelectedState } from './usePinsDataSync.utils';
import { IPinsLayer } from './usePinsLayer';
import { updateMapSelectionFeature } from '../../../../common/actions/mapSelection/mapSelection';
import { setDominantSubdomain } from '../../../../common/actions/results';
import { selectFreckles } from '../../../../common/selectors/freckles/selectFreckles';
import { selectResultsDominantSubdomain } from '../../../../common/selectors/results';
import { IFrecklesFeaturesMap } from '../../../../common/types/freckles';
import { IFeaturesMap, IRBushItem, TFeature } from '../../../../common/types/map';
import { IApplicationState, TThunkDispatch } from '../../../../common/types/redux';
import {
  expandClusterPinFeature,
  geoJsonFeatureToRBushItem,
  geoJsonFrecklesFeatureToRBushItem,
} from '../../../../common/utils/features';
import { mode } from '../../../../common/utils/mode';
import { getTilesToUpdate } from '../../../../common/utils/tiles';
import { selectActiveOfferId, selectMapBounds } from '../../../selectors';
import { selectFeaturesForPrecision } from '../../../selectors/features/selectFeaturesForPrecision';
import { TMapBounds } from '../../../types/mapBounds';
import { getFeatureSignature } from '../utils/getFeatureSignature';

interface IUsePinsDataSyncParams extends IPinsLayer {
  map: YMaps.Map;
  isDetailsVisible: boolean;
}

// Чуть больше троттла синка mapBounds (300мс в useBoundsSync): cleanup эффекта
// переносит ребилд, пока серия зумов (колесо/тач) продолжает менять стор,
// поэтому пересборка выполняется один раз после окончания серии и вне анимации
const PINS_REBUILD_DEBOUNCE_MS = 350;

export function usePinsDataSync({
  map,
  rbush,
  rbushFreckles,
  pointsLayer,
  frecklesPointsLayer,
  hotspotsLayer,
  balloonManager,
  isDetailsVisible,
}: IUsePinsDataSyncParams): void {
  const dispatch = useDispatch<TThunkDispatch>();
  const { bounds, zoom, precision } = useSelector<IApplicationState, TMapBounds>(selectMapBounds);
  const mapZoom = useMapZoom(map);
  const maxPinsPerCluster = useMaxPinsPerCluster();
  const activeOfferId = useSelector(selectActiveOfferId);
  const deviceType = useDeviceType();
  const paginationOfferRealtyId = deviceType === 'phone' ? null : activeOfferId;
  const subdomain = useSelector<IApplicationState, string>(selectResultsDominantSubdomain);

  const features = useSelector<IApplicationState, IFeaturesMap>(selectFeaturesForPrecision);
  const isFeaturesBucketReady = useSelector<IApplicationState, boolean>(state =>
    Boolean(state.map.features[state.map.mapBounds.precision]),
  );
  const frecklesFeatures: IFrecklesFeaturesMap = useSelector(selectFreckles);

  const subdomainFeatures = useSelector<IApplicationState, IFeaturesMap>(state =>
    pathOr<IFeaturesMap>({}, [precision, 'features'], state.map.features),
  );

  const propertiesFeatures = useSelector<IApplicationState, IFeaturesMap>(state =>
    state.map.features[state.map.mapBounds.precision] ? state.map.features[state.map.mapBounds.precision].features : {},
  );
  const prevFeaturesRef = React.useRef<IFeaturesMap>(propertiesFeatures);
  const prevPaginationOfferRealtyIdRef = React.useRef(paginationOfferRealtyId);
  const rebuildSignatureRef = React.useRef('');
  const lastFeaturesRef = React.useRef<IFeaturesMap>(features);
  const frecklesSignatureRef = React.useRef<string | null>(null);
  const areFrecklesVisible = mapZoom < 13;

  React.useEffect(() => {
    if (!bounds) {
      return;
    }

    const featuresInBbox = rbush.search({
      minX: bounds[0][0],
      minY: bounds[0][1],
      maxX: bounds[1][0],
      maxY: bounds[1][1],
    });

    const nextSubdomain = mode(featuresInBbox.map(f => f.feature.properties.subdomain));
    if (!nextSubdomain || nextSubdomain === subdomain) {
      return;
    }

    dispatch(setDominantSubdomain(nextSubdomain));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bounds, subdomainFeatures]);

  React.useEffect(() => {
    const prevFeatures = prevFeaturesRef.current;
    const hasPaginationChanged = paginationOfferRealtyId !== prevPaginationOfferRealtyIdRef.current;
    const updatedFeatures = Object.values(propertiesFeatures)
      .filter(({ id, properties }) => {
        const prevFeature = prevFeatures[id];
        if (!prevFeature) {
          return false;
        }

        // После ответа карты isActive может отсутствовать, но выбор оффера сохраняется.
        return (
          !equals(properties, prevFeature.properties) ||
          (hasPaginationChanged && properties.offers.some(offer => offer.realtyId === activeOfferId))
        );
      })
      .map(feature => getFeatureWithSelectedState(feature, activeOfferId));

    // в сторе у фич координаты центра кластера, в rbush — раскрытые координаты пинов,
    // поэтому поиск только по id (объектов ≤ ~28, линейный обход дешёвый)
    const rbushItemsAll = rbush.all();
    const rbushItemsToUpdate: IRBushItem[] = [];
    for (const feature of updatedFeatures) {
      const featureId = feature.id as string;
      const featureInRbush = rbushItemsAll.find(({ feature: { id } }) => id === featureId);
      if (featureInRbush) {
        featureInRbush.feature.properties = feature.properties;
        rbushItemsToUpdate.push(featureInRbush);

        // балун-пин перекрашиваем точечно и синхронно (как hover), не дожидаясь дебаунс-пересборки;
        // getById отсекает фичи без цены и ещё не добавленные в менеджер после смены зума
        if (balloonManager.objectManager.objects.getById<TFeature>(featureId)) {
          balloonManager.updateBalloonProperties(
            featureId,
            feature.properties,
            maxPinsPerCluster,
            paginationOfferRealtyId,
          );
        }
      }
    }

    if (rbushItemsToUpdate.length > 0) {
      const tilesToUpdate = getTilesToUpdate({
        rbushItems: rbushItemsToUpdate,
        bounds,
        zoom,
        includeShadow: true,
      });
      pointsLayer.layer.update(tilesToUpdate);
    }

    prevFeaturesRef.current = propertiesFeatures;
    prevPaginationOfferRealtyIdRef.current = paginationOfferRealtyId;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [propertiesFeatures, paginationOfferRealtyId, activeOfferId]);

  React.useEffect(() => {
    // Пересборка отложена и схлопывается (cleanup отменяет невыполненную):
    // removeAll/add в ObjectManager во время зум-анимации карты теряются рендером ymaps,
    // а смена зума и приезд свежих фич происходят почти одновременно — дебаунс
    // выполняет одну пересборку после завершения анимации вместо двух во время неё.
    const timeoutId = window.setTimeout(() => {
      // При смене зума precision меняется раньше, чем приезжает ответ бека — бакета фич
      // ещё нет в сторе. Чтобы слой пинов не исчезал на время полёта запроса, раскрываем
      // последний известный набор кластеров: их центры корректны на любом зуме.
      // Если бакет есть, но пуст — это честно пустой ответ, пины снимаются.
      const isTransitionalEmptyBucket = !isFeaturesBucketReady && Object.keys(features).length === 0;
      const sourceFeatures = isTransitionalEmptyBucket ? lastFeaturesRef.current : features;

      if (Object.keys(sourceFeatures).length > 0) {
        lastFeaturesRef.current = sourceFeatures;
      }

      // На один зум-жест эффект срабатывает дважды (смена зума, затем приезд фич из ответа) —
      // если зум, лимит, геометрия и состав пинов не изменились, пересборка не нужна.
      // Выбор, пагинация и визуальные флаги обновляются точечно в эффекте выше.
      const signature = `${mapZoom}|${precision}|${maxPinsPerCluster}|${Object.values(sourceFeatures)
        .map(getFeatureSignature)
        .join(';')}`;

      if (signature === rebuildSignatureRef.current) {
        return;
      }
      rebuildSignatureRef.current = signature;

      // Для rbush и превью пересчитываем геокоординаты слотов на текущем зуме.
      // BalloonManager пропустит перерисовку, если данные самих пинов не изменились.
      const expandedFeatures = Object.values(sourceFeatures)
        .map(feature => getFeatureWithSelectedState(feature, activeOfferId))
        .map(feature => expandClusterPinFeature(feature, mapZoom, maxPinsPerCluster))
        .filter((feature): feature is TFeature => feature !== null);
      const nextSelectedFeature = expandedFeatures.find(feature => feature.properties.isActive);
      const rbushItems = expandedFeatures.map(feature => geoJsonFeatureToRBushItem(feature));

      rbush.clear();

      if (rbushItems.length === 0) {
        // updateBalloons по пустому rbush снимает все балуны с карты
        balloonManager.updateBalloons(maxPinsPerCluster, paginationOfferRealtyId);
        pointsLayer.layer.update();

        return;
      }

      rbush.load(rbushItems);

      hotspotsLayer.layer.update();
      pointsLayer.layer.update();
      balloonManager.updateBalloons(maxPinsPerCluster, paginationOfferRealtyId);

      if (nextSelectedFeature && activeOfferId != null) {
        dispatch(
          updateMapSelectionFeature({ realtyId: activeOfferId, feature: { ...nextSelectedFeature, precision } }),
        );
      }
    }, PINS_REBUILD_DEBOUNCE_MS);

    return () => window.clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [features, isFeaturesBucketReady, mapZoom, precision, maxPinsPerCluster, activeOfferId, paginationOfferRealtyId]);

  React.useEffect(() => {
    // В ответах у веснушек случайные id. Сравниваем координаты: одинаковые точки
    // не требуют сброса тайлов ни после зума, ни после повторного ответа.
    const nextFeatures = areFrecklesVisible ? Object.values(frecklesFeatures) : [];
    const signature = nextFeatures
      .map(feature => feature.geometry.coordinates.join(','))
      .sort()
      .join(';');
    if (signature === frecklesSignatureRef.current) {
      return;
    }
    frecklesSignatureRef.current = signature;
    rbushFreckles.clear();
    rbushFreckles.load(nextFeatures.map(geoJsonFrecklesFeatureToRBushItem));
    frecklesPointsLayer.layer.update();
  }, [frecklesFeatures, areFrecklesVisible, rbushFreckles, frecklesPointsLayer]);
}
